/**
 * Store driving the general "Password required" on-connect prompt.
 *
 * `connectToDatabase` (a non-React store action) calls `requestConnectPassword`
 * when a saved connection needs a password it doesn't have; that returns a
 * promise which the globally-mounted ConnectPasswordPromptDialog resolves when
 * the user submits or cancels.
 */
import { create } from 'zustand';
import type { Connection } from '../../../types/electron';

export type ConnectPasswordResult = {
  password: string;
  /** Persist the password to the keychain so future connects don't prompt. */
  save: boolean;
};

type PendingRequest = {
  resolve: (result: ConnectPasswordResult | null) => void;
};

interface ConnectPasswordPromptState {
  isOpen: boolean;
  connection: Connection | null;
  pending: PendingRequest | null;
  request: (connection: Connection) => Promise<ConnectPasswordResult | null>;
  submit: (result: ConnectPasswordResult) => void;
  cancel: () => void;
}

export const useConnectPasswordPromptStore = create<ConnectPasswordPromptState>((set, get) => ({
  isOpen: false,
  connection: null,
  pending: null,

  request: (connection) =>
    new Promise<ConnectPasswordResult | null>((resolve) => {
      // Resolve any prompt already in flight as cancelled before replacing it.
      get().pending?.resolve(null);
      set({ isOpen: true, connection, pending: { resolve } });
    }),

  submit: (result) => {
    const pending = get().pending;
    set({ isOpen: false, connection: null, pending: null });
    pending?.resolve(result);
  },

  cancel: () => {
    const pending = get().pending;
    set({ isOpen: false, connection: null, pending: null });
    pending?.resolve(null);
  },
}));

/** Convenience for non-React callers (e.g. connectionStore.connectToDatabase). */
export function requestConnectPassword(
  connection: Connection
): Promise<ConnectPasswordResult | null> {
  return useConnectPasswordPromptStore.getState().request(connection);
}
